import React from "react";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { CustomizableAutocomplete } from "../../UI";
import colors from "../../../assets/theme/colors";
import { createEmptyCareGoal } from "../../../utils/supportProfile";
import { SUPPORT_PROFILE_OPTIONS } from "./supportProfileOptions";
import {
  CARE_GOAL_CATEGORY_OPTIONS,
  CARE_GOAL_SOURCE_OPTIONS,
  CARE_GOAL_STATUS_OPTIONS,
} from "./profileDropdownOptions";

const fieldCardSx = {
  p: { xs: 1.35, sm: 1.55 },
  borderRadius: 2.2,
  border: `1px solid ${colors.semantic.profileFlow.cardBorder}`,
  bgcolor: colors.semantic.profileFlow.cardBg,
};

const noteFieldSx = {
  "& .MuiInputLabel-root": {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: colors.semantic.profileFlow.fieldLabel,
  },
  "& .MuiInputBase-root": {
    minHeight: 54,
    borderRadius: "12px",
    bgcolor: colors.semantic.profileFlow.fieldBg,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: colors.semantic.profileFlow.cardBorder,
  },
  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: colors.semantic.profileFlow.focusBorder,
    borderWidth: "1px",
  },
};

const SectionCard = ({ title, helperText, emptyHint = "Nothing added yet — you can come back anytime.", children, empty = false }) => (
  <Box sx={fieldCardSx}>
    <Stack spacing={1.15}>
      <Box>
        <Typography sx={{ fontSize: "0.96rem", fontWeight: 800, color: colors.landing.heroText }}>
          {title}
        </Typography>
        <Typography sx={{ mt: 0.2, fontSize: "0.81rem", color: colors.semantic.ink2, lineHeight: 1.45 }}>
          {helperText}
        </Typography>
      </Box>

      {empty ? (
        <Box
          sx={{
            borderRadius: 1.8,
            border: `1px dashed ${colors.semantic.profileFlow.cardBorder}`,
            bgcolor: colors.semantic.profileFlow.nudgeBg,
            px: 1.15,
            py: 0.95,
          }}
        >
          <Typography sx={{ fontSize: "0.82rem", color: colors.semantic.ink2, lineHeight: 1.45 }}>
            {emptyHint}
          </Typography>
        </Box>
      ) : null}

      <Stack spacing={1.05}>
        {children}
      </Stack>
    </Stack>
  </Box>
);

const MultiTagField = ({
  label,
  helperText,
  placeholder,
  value = [],
  onChange,
  options = [],
  addText = "Add",
}) => (
  <CustomizableAutocomplete
    options={options}
    value={value}
    onChange={onChange}
    label={label}
    helperText={helperText}
    placeholder={placeholder}
    addText={addText}
    sx={noteFieldSx}
  />
);

const getGoalStatusLabel = (status = "active") => (
  status === "completed" ? "Completed" : status === "paused" ? "Paused" : "Active"
);

const SupportGoalsSection = ({
  supportProfile,
  setSupportProfile,
  childDisplayName,
  sectionGroup = "all",
  showIntro = true,
  supportOptions = SUPPORT_PROFILE_OPTIONS,
  sensoryIssues = [],
  onSensoryIssuesChange = null,
  sensoryOptions = [],
  behavioralTriggers = [],
  onBehavioralTriggersChange = null,
  triggerOptions = [],
}) => {
  const safeSupportProfile = supportProfile || {};
  const whoTheyAre = safeSupportProfile.whoTheyAre || {};
  const careGoals = Array.isArray(safeSupportProfile.careGoals) ? safeSupportProfile.careGoals : [];
  const titleName = String(childDisplayName || "this child").trim();

  const updateTopLevel = (key, value) => {
    setSupportProfile?.((current) => ({
      ...(current || {}),
      [key]: value,
    }));
  };

  const updateNested = (section, key, value) => {
    setSupportProfile?.((current) => ({
      ...(current || {}),
      [section]: {
        ...((current || {})[section] || {}),
        [key]: value,
      },
    }));
  };

  const updateCareGoal = (goalId, key, value) => {
    setSupportProfile?.((current) => ({
      ...(current || {}),
      careGoals: ((current || {}).careGoals || []).map((goal) => (
        goal.id === goalId ? { ...goal, [key]: value } : goal
      )),
    }));
  };

  const addCareGoal = () => {
    setSupportProfile?.((current) => ({
      ...(current || {}),
      careGoals: [
        ...((current || {}).careGoals || []),
        createEmptyCareGoal(),
      ],
    }));
  };

  const removeCareGoal = (goalId) => {
    setSupportProfile?.((current) => ({
      ...(current || {}),
      careGoals: ((current || {}).careGoals || []).filter((goal) => goal.id !== goalId),
    }));
  };

  const isEmpty =
    !safeSupportProfile.about
    && !whoTheyAre.favoriteActivities?.length
    && !whoTheyAre.interests?.length
    && !whoTheyAre.comfortItems?.length
    && !whoTheyAre.favoriteFoods?.length
    && !whoTheyAre.notes
    && !safeSupportProfile.whatHelps?.length
    && !safeSupportProfile.whatCanBeDifficult?.length
    && !safeSupportProfile.earlyWarningSigns?.length
    && !safeSupportProfile.thingsThatUsuallyHelp?.length
    && !safeSupportProfile.thingsToAvoid?.length
    && !safeSupportProfile.safetyConsiderations?.length
    && !safeSupportProfile.communicationStyle?.length
    && !safeSupportProfile.communicationNotes
    && !careGoals.length
    && !safeSupportProfile.familySupportTips?.length
    && !safeSupportProfile.schoolTherapySuggestions;
  const shouldShow = (group) => sectionGroup === "all" || sectionGroup === group;

  return (
    <Stack spacing={1.2}>
      {showIntro ? (
        <Box
          sx={{
            p: { xs: 1.45, sm: 1.75 },
            borderRadius: 2.5,
            border: `1px solid ${colors.semantic.profileFlow.cardBorder}`,
            bgcolor: colors.semantic.profileFlow.cardBg,
          }}
        >
          <Stack spacing={0.8}>
            <Typography sx={{ fontSize: "1.12rem", fontWeight: 800, color: colors.landing.heroText }}>
              Care Support
            </Typography>
            <Typography sx={{ fontSize: "0.86rem", color: colors.semantic.ink2, lineHeight: 1.5 }}>
              Help family and caregivers understand {titleName}’s personality, preferences, supports, and care priorities over time.
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: colors.semantic.profileFlow.completionText }}>
              You can update this anytime.
            </Typography>
            {isEmpty ? (
              <Box
                sx={{
                  mt: 0.3,
                  p: 1.05,
                  borderRadius: 2,
                  bgcolor: colors.semantic.profileFlow.nudgeBg,
                  border: `1px solid ${colors.semantic.profileFlow.cardBorder}`,
                }}
              >
                <Typography sx={{ fontSize: "0.82rem", color: colors.semantic.ink2 }}>
                  Nothing added yet — you can come back anytime.
                </Typography>
              </Box>
            ) : null}
          </Stack>
        </Box>
      ) : null}

      {shouldShow("daily") ? (
        <>
          <SectionCard
            title={`Who ${titleName} Is`}
            helperText={`What makes ${titleName} feel happy, comfortable, and most like themselves.`}
            empty={Boolean(isEmpty)}
          >
            <MultiTagField
              label="Favorite activities"
              helperText="Things that feel fun, calming, or energizing."
              placeholder="Music, puzzles, outside time..."
              value={whoTheyAre.favoriteActivities || []}
              onChange={(event, nextValue) => updateNested("whoTheyAre", "favoriteActivities", nextValue)}
              options={supportOptions.favoriteActivities}
              addText="Add activity"
            />
            <MultiTagField
              label="Interests"
              helperText="Topics, characters, or activities they love."
              placeholder="Dinosaurs, drawing, trains..."
              value={whoTheyAre.interests || []}
              onChange={(event, nextValue) => updateNested("whoTheyAre", "interests", nextValue)}
              options={supportOptions.interests}
              addText="Add interest"
            />
            <MultiTagField
              label="Comfort items"
              helperText="Things that help them feel safe and settled."
              placeholder="Headphones, blanket, photos..."
              value={whoTheyAre.comfortItems || []}
              onChange={(event, nextValue) => updateNested("whoTheyAre", "comfortItems", nextValue)}
              options={supportOptions.comfortItems}
              addText="Add comfort item"
            />
            <MultiTagField
              label="Favorite foods"
              helperText="Foods they usually enjoy or ask for."
              placeholder="Waffles, pasta, yogurt..."
              value={whoTheyAre.favoriteFoods || []}
              onChange={(event, nextValue) => updateNested("whoTheyAre", "favoriteFoods", nextValue)}
              options={supportOptions.favoriteFoods}
              addText="Add food"
            />
            <TextField
              label="Notes"
              placeholder="Anything else that helps people know what makes them feel most like themselves."
              multiline
              minRows={3}
              fullWidth
              value={whoTheyAre.notes || ""}
              onChange={(event) => updateNested("whoTheyAre", "notes", event.target.value)}
              sx={noteFieldSx}
            />
          </SectionCard>

          <SectionCard
            title="What Helps"
            helperText={`Things that help ${titleName} feel calm, connected, supported, or comfortable.`}
            empty={Boolean(isEmpty)}
          >
            <MultiTagField
              label="Helpful supports"
              helperText="Small things that make a big difference."
              placeholder="Quiet time, reminders, rest breaks..."
              value={safeSupportProfile.whatHelps || []}
              onChange={(event, nextValue) => updateTopLevel("whatHelps", nextValue)}
              options={supportOptions.whatHelps}
              addText="Add support"
            />
          </SectionCard>
        </>
      ) : null}

      {shouldShow("patterns") ? (
        <SectionCard
          title="Behavior Support"
          helperText={`Early signs, helpful responses, and safety context caregivers should know for ${titleName}.`}
          empty={Boolean(isEmpty)}
        >
          <MultiTagField
            label="Early warning signs"
            helperText="What are the first signs this person may be becoming overwhelmed, frustrated, tired, or uncomfortable?"
            placeholder="Covers ears, pacing, asks to leave..."
            value={safeSupportProfile.earlyWarningSigns || []}
            onChange={(event, nextValue) => updateTopLevel("earlyWarningSigns", nextValue)}
            options={supportOptions.earlyWarningSigns}
            addText="Add sign"
          />
          <MultiTagField
            label="Things that usually help"
            helperText="Small actions that often help during difficult moments."
            placeholder="Visual timer, headphones, movement break..."
            value={safeSupportProfile.thingsThatUsuallyHelp || []}
            onChange={(event, nextValue) => updateTopLevel("thingsThatUsuallyHelp", nextValue)}
            options={supportOptions.thingsThatUsuallyHelp}
            addText="Add help"
          />
          <MultiTagField
            label="Things to avoid"
            helperText="Things that tend to make situations worse."
            placeholder="Raising voice, rushing, crowding..."
            value={safeSupportProfile.thingsToAvoid || []}
            onChange={(event, nextValue) => updateTopLevel("thingsToAvoid", nextValue)}
            options={supportOptions.thingsToAvoid}
            addText="Add avoid"
          />
          <MultiTagField
            label="Safety considerations"
            helperText="Important safety information caregivers should know."
            placeholder="Wandering / elopement, fall risk, food allergies..."
            value={safeSupportProfile.safetyConsiderations || []}
            onChange={(event, nextValue) => updateTopLevel("safetyConsiderations", nextValue)}
            options={supportOptions.safetyConsiderations}
            addText="Add safety note"
          />
          <CustomizableAutocomplete
            options={sensoryOptions}
            value={sensoryIssues}
            onChange={onSensoryIssuesChange}
            label="Sensory or comfort needs"
            addText="Add need"
          />
          <CustomizableAutocomplete
            options={triggerOptions}
            value={behavioralTriggers}
            onChange={onBehavioralTriggersChange}
            label="Triggers and hard moments"
            addText="Add hard thing"
          />
          <MultiTagField
            label="Hard moments"
            helperText="Situations that may feel hard, tiring, confusing, or overwhelming."
            placeholder="Loud places, waiting, pain, fatigue..."
            value={safeSupportProfile.whatCanBeDifficult || []}
            onChange={(event, nextValue) => updateTopLevel("whatCanBeDifficult", nextValue)}
            options={supportOptions.whatCanBeDifficult}
            addText="Add difficulty"
          />
        </SectionCard>
      ) : null}

      {shouldShow("daily") ? (
        <SectionCard
          title="Communication & Support Style"
          helperText={`How ${titleName} communicates needs and what support works best.`}
          empty={Boolean(isEmpty)}
        >
          <MultiTagField
            label="Communication style"
            helperText="Helpful reminders about how they tend to communicate."
            placeholder="Needs processing time, prefers choices..."
            value={safeSupportProfile.communicationStyle || []}
            onChange={(event, nextValue) => updateTopLevel("communicationStyle", nextValue)}
            options={supportOptions.communicationStyle}
            addText="Add style"
          />
          <TextField
            label="Support notes"
            placeholder="They may go quiet when overwhelmed. Visual reminders help."
            multiline
            minRows={3}
            fullWidth
            value={safeSupportProfile.communicationNotes || ""}
            onChange={(event) => updateTopLevel("communicationNotes", event.target.value)}
            sx={noteFieldSx}
          />
        </SectionCard>
      ) : null}

      {shouldShow("goals") ? (
        <>
          <SectionCard
            title="Goals & Progress"
            helperText={`Connect everyday moments to longer-term progress for ${titleName}.`}
            empty={Boolean(isEmpty)}
          >
            <Stack spacing={1}>
              {careGoals.map((goal, index) => (
                <Box
                  key={goal.id || index}
                  sx={{
                    p: 1.15,
                    borderRadius: 2,
                    border: `1px solid ${colors.semantic.profileFlow.cardBorder}`,
                    bgcolor: "#FFFFFF",
                  }}
                >
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography sx={{ flex: 1, fontSize: "0.84rem", fontWeight: 800, color: colors.landing.heroText }}>
                        Goal {index + 1}
                      </Typography>
                      <IconButton
                        aria-label="Remove care goal"
                        size="small"
                        onClick={() => removeCareGoal(goal.id)}
                        sx={{ color: colors.semantic.ink2 }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <TextField
                      label="Goal"
                      placeholder="Transition between activities"
                      fullWidth
                      value={goal.title || ""}
                      onChange={(event) => updateCareGoal(goal.id, "title", event.target.value)}
                      sx={noteFieldSx}
                    />
                    <TextField
                      label="Description"
                      placeholder="Short explanation of what the care team is supporting."
                      multiline
                      minRows={2}
                      fullWidth
                      value={goal.description || ""}
                      onChange={(event) => updateCareGoal(goal.id, "description", event.target.value)}
                      sx={noteFieldSx}
                    />
                    <TextField
                      label="Success Criteria"
                      placeholder="Completes transitions with one or fewer prompts in most opportunities."
                      multiline
                      minRows={2}
                      fullWidth
                      value={goal.successCriteria || ""}
                      onChange={(event) => updateCareGoal(goal.id, "successCriteria", event.target.value)}
                      sx={noteFieldSx}
                    />
                    <TextField
                      label="Support Strategy"
                      placeholder="Use visual timer and 2-minute warning."
                      multiline
                      minRows={2}
                      fullWidth
                      value={goal.supportStrategy || ""}
                      onChange={(event) => updateCareGoal(goal.id, "supportStrategy", event.target.value)}
                      sx={noteFieldSx}
                    />
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <TextField
                        select
                        label="Category"
                        fullWidth
                        value={goal.category || ""}
                        onChange={(event) => updateCareGoal(goal.id, "category", event.target.value)}
                        sx={noteFieldSx}
                      >
                        {CARE_GOAL_CATEGORY_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        select
                        label="Source"
                        fullWidth
                        value={goal.source || ""}
                        onChange={(event) => updateCareGoal(goal.id, "source", event.target.value)}
                        sx={noteFieldSx}
                      >
                        {CARE_GOAL_SOURCE_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        select
                        label="Status"
                        fullWidth
                        value={goal.status || "active"}
                        onChange={(event) => updateCareGoal(goal.id, "status", event.target.value)}
                        sx={noteFieldSx}
                      >
                        {CARE_GOAL_STATUS_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <TextField
                        label="Start date"
                        type="date"
                        fullWidth
                        value={goal.startDate || ""}
                        onChange={(event) => updateCareGoal(goal.id, "startDate", event.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={noteFieldSx}
                      />
                      <TextField
                        label="Target date"
                        type="date"
                        fullWidth
                        value={goal.targetDate || ""}
                        onChange={(event) => updateCareGoal(goal.id, "targetDate", event.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={noteFieldSx}
                      />
                    </Stack>
                  </Stack>
                </Box>
              ))}
            </Stack>
            <Button
              onClick={addCareGoal}
              variant="outlined"
              sx={{
                alignSelf: "flex-start",
                borderRadius: 999,
                px: 1.6,
                py: 0.75,
                fontWeight: 800,
                color: colors.brand.primary,
                borderColor: colors.brand.primary,
                textTransform: "none",
              }}
            >
              Add care goal
            </Button>
          </SectionCard>

          <SectionCard
            title="How Family Can Help"
            helperText={`Simple ways relatives, caregivers, or trusted helpers can support ${titleName}.`}
            empty={Boolean(isEmpty)}
          >
            <MultiTagField
              label="Helpful tips"
              helperText="Practical things family can do."
              placeholder="Give warnings before changes, keep voice calm..."
              value={safeSupportProfile.familySupportTips || []}
              onChange={(event, nextValue) => updateTopLevel("familySupportTips", nextValue)}
              options={supportOptions.familySupportTips}
              addText="Add tip"
            />
          </SectionCard>

          <SectionCard
            title="Important Notes for Care Team"
            helperText={`Optional context that helps caregivers support ${titleName} consistently.`}
            empty={Boolean(isEmpty)}
          >
            <TextField
              label="Important notes"
              placeholder="Shared notes from providers, school, family, or other trusted helpers."
              multiline
              minRows={4}
              fullWidth
              value={safeSupportProfile.schoolTherapySuggestions || ""}
              onChange={(event) => updateTopLevel("schoolTherapySuggestions", event.target.value)}
              sx={noteFieldSx}
            />
          </SectionCard>
        </>
      ) : null}
    </Stack>
  );
};

export default SupportGoalsSection;
